import React from 'react';
import { DASS21_SUBTITLE_MAPPING } from '../config/assessmentCompletionConfigs';
import { useNavigate } from 'react-router-dom';
import { getProportionalScoreColor, getNumericalScoreDots } from '../utils/scoringUtils';
import '../styles/assessment-completion-overrides.css';

// Define recommendation types
export interface RecommendationObject {
  category?: string;
  title?: string;
  description: string;
  priority?: 'high' | 'medium' | 'low';
  resources?: Array<{
    title: string;
    url: string;
    type: string;
  }>;
}

export type Recommendation = string | RecommendationObject;

export interface AssessmentCompletionConfig {
  // Assessment type information
  assessmentType: string;
  assessmentName: string;
  assessmentDisplayName: string;

  // Navigation routes
  redoRoute: string;
  completeRoute: string;

  // Score configuration
  maxScore?: number;
  scoreLabel: string;
  hideScore?: boolean;
  subtitleMapping: {
    High: string;
    Moderate: string;
    Low: string;
    Completed?: string;
  };

  // Visual customization
  visualMessage: string;
  resultsTitle: string;

  // Data structure
  scores: any;
  results?: any;
  selectedCategory?: number;
  
  // Function to get score color
  switch?: (level: string) => string;
};

interface AssessmentCompletionTemplateProps {
  config: AssessmentCompletionConfig;
}

const AssessmentCompletionTemplate: React.FC<AssessmentCompletionTemplateProps> = ({ config }) => {
  const navigate = useNavigate();

  const getScoreColor = (level: string): string => {
    // CBI: Low = GREEN (good), Moderate = ORANGE, High = RED (bad)
    const isCBIAssessment = config.assessmentDisplayName?.toLowerCase().includes('cbi') || config.assessmentDisplayName?.toLowerCase().includes('burnout');
    if (isCBIAssessment) {
      switch (level.toLowerCase()) {
        case 'low': return '#059669'; // Low burnout = GREEN (good)
        case 'moderate': return '#ea580c'; // Moderate = ORANGE
        case 'high': return '#dc2626'; // High burnout = RED (bad)
        default: return '#6B7280';
      }
    }
    
    // UWES Assessment: displays as Burnout - Low = GREEN (good), Moderate = ORANGE, High = RED (bad)
    const isUWESAssessment = config.assessmentDisplayName?.toLowerCase().includes('uwes');
    if (isUWESAssessment) {
      switch (level.toLowerCase()) {
        case 'low': return '#059669'; // Low burnout = GREEN (good)
        case 'moderate': return '#ea580c'; // Moderate = ORANGE
        case 'high': return '#dc2626'; // High burnout = RED (bad)
        default: return '#6B7280';
      }
    }
    
    // Employee Engagement Assessment: displays as Engagement - High = GREEN (good), Moderate = ORANGE, Low = RED (bad)
    const isEmployeeEngagementAssessment = config.assessmentDisplayName?.toLowerCase().includes('employee engagement') || config.assessmentType === 'occupational';
    if (isEmployeeEngagementAssessment) {
      switch (level.toLowerCase()) {
        case 'high': return '#059669'; // High engagement = GREEN (good)
        case 'moderate': return '#ea580c'; // Moderate = ORANGE
        case 'low': return '#dc2626'; // Low engagement = RED (bad)
        default: return '#6B7280';
      }
    }
    
    // Gender Sensitivity: High = GREEN (good), Moderate = ORANGE, Low = RED (bad)
    const isGenderSensitivity = config.assessmentType === 'emotio-psycho-rational' && (config.selectedCategory === 4 || (config.scores.category4 !== undefined && config.scores.category4 > 0));
    if (isGenderSensitivity) {
      switch (level.toLowerCase()) {
        case 'high': return '#059669'; // High = GREEN (good)
        case 'moderate': return '#ea580c'; // Moderate = ORANGE
        case 'low': return '#dc2626'; // Low = RED (bad)
        default: return '#6B7280';
      }
    }
    // Emotio-Psycho-Rational (other): High = RED (bad), Moderate = ORANGE, Low = GREEN (good)
    if (config.assessmentType === 'emotio-psycho-rational') {
      switch (level.toLowerCase()) {
        case 'high': return '#dc2626'; // High = RED (bad)
        case 'moderate': return '#ea580c'; // Moderate = ORANGE
        case 'low': return '#059669'; // Low = GREEN (good)
        default: return '#6B7280';
      }
    }
    // Check if this is an environmental assessment
    const isEnvironmentalAssessment = config.assessmentType === 'environmental';
    if (isEnvironmentalAssessment) {
      // For environmental assessments, high score = good = GREEN
      switch (level.toLowerCase()) {
        case 'high': return '#059669'; // High score = GREEN
        case 'moderate': return '#ea580c';
        case 'low': return '#dc2626'; // Low score = RED
        default: return '#6B7280';
      }
    }
    
    // Check if this is an IPAQ-SF Physical Activity assessment
    const isIPAQ = config.assessmentType === 'physical-ipaq' ||
      config.assessmentDisplayName?.toLowerCase().includes('ipaq') ||
      config.assessmentName?.toLowerCase().includes('ipaq');
    if (isIPAQ) {
      // For IPAQ-SF: High activity = GREEN, Moderate = YELLOW, Low = RED
      switch (level.toLowerCase()) {
        case 'high': return '#059669';
        case 'moderate': return '#ea580c';
        case 'low': return '#dc2626';
        default: return '#6B7280';
      }
    }
    
    // Check if this is a PSQI Sleep Quality assessment
    const isPSQI = config.assessmentType === 'physical-psqi' ||
      config.assessmentDisplayName?.toLowerCase().includes('psqi') ||
      config.assessmentName?.toLowerCase().includes('psqi');
    if (isPSQI) {
      // For PSQI: High = good sleep = GREEN, Moderate = poor sleep = ORANGE, Low = very poor sleep = RED
      switch (level.toLowerCase()) {
        case 'high': return '#059669'; // Good sleep quality = GREEN
        case 'moderate': return '#ea580c'; // Poor sleep quality = ORANGE
        case 'low': return '#dc2626'; // Very poor sleep quality = RED
        default: return '#6B7280';
      }
    }
    
    // Check if this is a financial assessment (custom logic: high score = green, low score = red)
    const isFinancialAssessment = config.assessmentType === 'financial';
    if (isFinancialAssessment) {
      // For financial assessments: high score = low distress = GREEN, moderate = ORANGE, low score = high distress = RED
      switch (level.toLowerCase()) {
        case 'high': return '#059669'; // High score = GREEN (Low Financial Distress)
        case 'moderate': return '#ea580c'; // Moderate = ORANGE
        case 'low': return '#dc2626'; // Low score = RED (High Financial Distress)
        default: return '#6B7280';
      }
    }

    // Check if this is a nutritional assessment
    const isNutritionalAssessment = config.assessmentType === 'nutritional';
    if (isNutritionalAssessment) {
      // For nutritional assessments, high score = good = GREEN, moderate = ORANGE, low = RED
      switch (level.toLowerCase()) {
        case 'high': return '#059669'; // High score = GREEN
        case 'moderate': return '#ea580c'; // Moderate = ORANGE
        case 'low': return '#dc2626'; // Low score = RED
        default: return '#6B7280';
      }
    }

    // Check if this is a general physical assessment
    const isGeneralPhysicalAssessment = config.assessmentType === 'physical-general';
    if (isGeneralPhysicalAssessment) {
      // For general physical assessment: high = green, moderate = orange, low = red
      switch (level.toLowerCase()) {
        case 'high': return '#059669'; // High = GREEN
        case 'moderate': return '#ea580c'; // Moderate = ORANGE
        case 'low': return '#dc2626'; // Low = RED
        default: return '#6B7280';
      }
    }

    // Check if this is a spiritual assessment
    const isSpiritualAssessment = config.assessmentDisplayName?.toLowerCase().includes('spiritual') || config.assessmentType === 'spiritual' || config.assessmentType === 'fasdl' || config.assessmentType === 'facit-sp-12';
    if (isSpiritualAssessment) {
      // For spiritual assessments, high = green, moderate = yellow, low = red
      switch (level.toLowerCase()) {
        case 'high': return '#059669'; // High spiritual integration = GREEN
        case 'moderate': return '#ea580c'; // Moderate = YELLOW
        case 'low': return '#dc2626'; // Low spiritual integration = RED
        default: return '#6B7280';
      }
    }
    
    // Default: Religious coping - High = GREEN, Moderate = ORANGE, Low = RED
    switch (level.toLowerCase()) {
      case 'high': return '#059669'; // High religious coping = GREEN
      case 'moderate': return '#ea580c';
      case 'low': return '#dc2626'; // Low religious coping = RED
      default: return '#6B7280';
    }
  };

  const getScoreDots = (level: string, score?: number, maxScore?: number) => {
        // PHQ-9: Use filledDots if present in results
        if (config.assessmentType === 'emotio-psycho-rational' && config.selectedCategory === 2 && config.results.filledDots) {
          return config.results.filledDots;
        }
        // MSPSS: Multidimensional Scale of Perceived Social Support
        const isMSPSS = (config.assessmentDisplayName?.toLowerCase().includes('multidimensional scale of perceived social support')
          || config.selectedCategory === 2);
        if (isMSPSS) {
          // High = 3 dots, Moderate = 2 dots, Low = 1 dot
          switch (level.toLowerCase()) {
            case 'high': return 3;
            case 'moderate': return 2;
            case 'low': return 1;
            default: return 1;
          }
        }
    // CBI: high score = High Burnout Risk (3 red dots), low score = Low Burnout Risk (1 green dot), moderate = 2 orange dots
    const isCBIAssessment = config.assessmentDisplayName?.toLowerCase().includes('cbi');
    if (isCBIAssessment) {
      if (level.toLowerCase() === 'high') return 3;
      if (level.toLowerCase() === 'moderate') return 2;
      if (level.toLowerCase() === 'low') return 1;
      return 1;
    }
    // Gender Sensitivity: 3 dots for low, 1 for high
    const isGenderSensitivity = config.assessmentDisplayName?.toLowerCase().includes('gender') || (config.scores.category4 !== undefined && config.scores.category4 > 0);
    if (isGenderSensitivity) {
      switch (level.toLowerCase()) {
        case 'high': return 1;
        case 'moderate': return 2;
        case 'low': return 3;
        default: return 1;
      }
    }
    // DASS-21, PHQ-9, GAD-7: 1 dot for green (good/low), 3 dots for red (bad/high)
    const isMentalHealthSymptomAssessment = config.assessmentType === 'emotio-psycho-rational' &&
      (config.scores.category1 !== undefined || config.scores.category2 !== undefined || config.scores.category3 !== undefined || config.scores.subscales !== undefined);
    if (isMentalHealthSymptomAssessment) {
      // Green (good/low symptoms) = 1 dot, Red (bad/high symptoms) = 3 dots
      if (level.toLowerCase() === 'high') return 3; // Bad mental health = 3 dots
      if (level.toLowerCase() === 'moderate') return 2;
      if (level.toLowerCase() === 'low') return 1; // Good mental health = 1 dot
      return 1;
    }
    // SWLS, PIL: 3 dots for high, 2 for moderate, 1 for low
    const isSWLSAssessment = config.assessmentDisplayName?.toLowerCase().includes('swls');
    const isPILAssessment = config.assessmentDisplayName?.toLowerCase().includes('pil');
    if (isSWLSAssessment || isPILAssessment) {
      if (level.toLowerCase() === 'high') return 3;
      if (level.toLowerCase() === 'moderate') return 2;
      if (level.toLowerCase() === 'low') return 1;
      return 1;
    }
    // UWES (Utrecht Work Engagement Scale): 3 dots for high, 2 for moderate, 1 for low
    const isUWESAssessment = config.assessmentType === 'uwes' ||
      config.assessmentDisplayName?.toLowerCase().includes('uwes') || 
      (config.scores.category2 !== undefined && config.scores.category2 > 0 && 
       config.scores.category1 === 0 && config.scores.category3 === 0);
    if (isUWESAssessment) {
      if (level.toLowerCase() === 'high') return 3;
      if (level.toLowerCase() === 'moderate') return 2;
      if (level.toLowerCase() === 'low') return 1;
      return 3;
    }

    // Employee Engagement: 3 dots for high, 2 for moderate, 1 for low
    const isEngagementAssessment = config.assessmentType === 'employee-engagement' ||
      config.assessmentDisplayName?.toLowerCase().includes('engagement');
    if (isEngagementAssessment) {
      if (level.toLowerCase() === 'high') return 3;
      if (level.toLowerCase() === 'moderate') return 2;
      if (level.toLowerCase() === 'low') return 1;
      return 3;
    }

    // TVAS (Traditional Values Assessment Scale): High = 3 dots, Moderate = 2, Low = 1
    const isTVASAssessment = config.assessmentType === 'religio-traditio-cultural' &&
      config.scores.category3 > 0 && config.scores.category1 === 0 && config.scores.category2 === 0;
    if (isTVASAssessment) {
      if (level.toLowerCase() === 'high') return 3;
      if (level.toLowerCase() === 'moderate') return 2;
      if (level.toLowerCase() === 'low') return 1;
      return 1;
    }

    // Brief RCOPE (Religious Coping): High = 3 dots (good), Moderate = 2, Low = 1
    const isBriefRCOPEAssessment = config.assessmentType === 'religio-traditio-cultural' &&
      config.scores.category1 > 0 && config.scores.category2 === 0 && config.scores.category3 === 0;
    if (isBriefRCOPEAssessment) {
      if (level.toLowerCase() === 'high') return 3;
      if (level.toLowerCase() === 'moderate') return 2;
      if (level.toLowerCase() === 'low') return 1;
      return 1;
    }

    // Environmental: High = 3 dots, Moderate = 2, Low = 1
    const isEnvironmentalAssessment = config.assessmentType === 'environmental';
    if (isEnvironmentalAssessment) {
      if (level.toLowerCase() === 'high') return 3;
      if (level.toLowerCase() === 'moderate') return 2;
      if (level.toLowerCase() === 'low') return 1;
      return 1;
    }

    // IPAQ-SF: High = 3 dots (green), Moderate = 2 dots (yellow), Low = 1 dot (red)
    const isIPAQ = config.assessmentType === 'physical-ipaq' ||
      config.assessmentDisplayName?.toLowerCase().includes('ipaq') ||
      config.assessmentName?.toLowerCase().includes('ipaq');
    if (isIPAQ) {
      if (level.toLowerCase() === 'high') return 3;
      if (level.toLowerCase() === 'moderate') return 2;
      if (level.toLowerCase() === 'low') return 1;
      return 1;
    }

    // PSQI: High (poor sleep) = 3 dots (red), Moderate = 2 dots (yellow), Low (good sleep) = 1 dot (green)
    const isPSQI = config.assessmentType === 'physical-psqi' ||
      config.assessmentDisplayName?.toLowerCase().includes('psqi') ||
      config.assessmentName?.toLowerCase().includes('psqi');
    if (isPSQI) {
      if (level.toLowerCase() === 'high') return 3;
      if (level.toLowerCase() === 'moderate') return 2;
      if (level.toLowerCase() === 'low') return 1;
      return 1;
    }

    // Check if this is a financial assessment (general)
    const isFinancialAssessment = config.assessmentType === 'financial';
    if (isFinancialAssessment) {
      // For financial assessments: High score (low distress) = 1 dot, Moderate = 2 dots, Low score (high distress) = 3 dots
      switch (level.toLowerCase()) {
        case 'high': return 1; // High score = 1 dot (Low Financial Distress)
        case 'moderate': return 2; // Moderate = 2 dots
        case 'low': return 3; // Low score = 3 dots (High Financial Distress)
        default: return 1;
      }
    }

    // Check if this is a nutritional assessment
    const isNutritionalAssessment = config.assessmentType === 'nutritional';
    if (isNutritionalAssessment) {
      // For nutritional assessments: 3 dots for high (good), 2 for moderate, 1 for low (bad)
      if (level.toLowerCase() === 'high') return 3;
      if (level.toLowerCase() === 'moderate') return 2;
      if (level.toLowerCase() === 'low') return 1;
      return 1;
    }

    // Check if this is a general physical assessment
    const isGeneralPhysicalAssessment = config.assessmentType === 'physical-general';
    if (isGeneralPhysicalAssessment) {
      // For general physical assessment: 3 dots for high (good), 2 for moderate, 1 for low (bad)
      if (level.toLowerCase() === 'high') return 3;
      if (level.toLowerCase() === 'moderate') return 2;
      if (level.toLowerCase() === 'low') return 1;
      return 1;
    }

    // Check if this is a spiritual assessment (FASDL, FACIT-Sp-12)
    const isSpiritualAssessment = config.assessmentDisplayName?.toLowerCase().includes('spiritual') || config.assessmentType === 'spiritual' || config.assessmentType === 'fasdl' || config.assessmentType === 'facit-sp-12';
    if (isSpiritualAssessment) {
      // For spiritual assessments: 3 dots for high, 2 for moderate, 1 for low
      if (level.toLowerCase() === 'high') return 3;
      if (level.toLowerCase() === 'moderate') return 2;
      if (level.toLowerCase() === 'low') return 1;
      return 1;
    }

    // Default: 1 dot for high, 2 for moderate, 3 for low
    switch (level.toLowerCase()) {
      case 'high': return 1;
      case 'moderate': return 2;
      case 'low': return 3;
      default: return 1;
    }
  };

  const getScoreExplanation = (): string => {
    const level = config.results.level?.toLowerCase() || 'moderate';
    
    // GENERAL PHYSICAL ASSESSMENT
    if (config.assessmentType === 'physical-general') {
      const physicalData = config.results?.physicalData || {};
      let explanation = '';
      
      if (level === 'high') {
        explanation = 'Your physical health assessment indicates you are in good overall health. Your vitals, activity level, and lifestyle habits are within healthy ranges. ';
      } else if (level === 'moderate') {
        explanation = 'Your physical health shows moderate results. While you have some good health indicators, there are areas where improvements could benefit your overall wellness. ';
      } else {
        explanation = 'Your physical health assessment indicates several areas that may need attention. Consider focusing on the recommended health modifications to improve your overall wellness. ';
      }
      
      if (physicalData.bmi) {
        const bmiCategory = physicalData.bmiCategory?.toLowerCase() || 'unknown';
        explanation += `Your BMI is ${physicalData.bmi.toFixed(1)}, which falls in the ${bmiCategory} weight category. `;
      }
      
      if (physicalData.smokingStatus) {
        if (physicalData.smokingStatus.toLowerCase() === 'smoker') {
          explanation += 'Smoking status significantly impacts your health risk profile. Cessation support is highly recommended. ';
        } else if (physicalData.smokingStatus.toLowerCase() === 'former smoker') {
          explanation += 'Your history as a former smoker is positive, and continuing to avoid smoking is important for maintaining health. ';
        }
      }
      
      if (physicalData.chronicConditions && physicalData.chronicConditions.length > 0) {
        explanation += `You reported ${physicalData.chronicConditions.length} chronic condition(s) that require ongoing management and monitoring. Regular check-ups and appropriate treatment are important.`;
      }
      
      return explanation;
    }
    
    // CBI/BURNOUT ASSESSMENT
    if (config.assessmentDisplayName?.toLowerCase().includes('cbi') || config.assessmentDisplayName?.toLowerCase().includes('burnout')) {
      if (level === 'high') {
        return 'Your burnout score is elevated, indicating significant work-related exhaustion and stress. This level of burnout may be affecting your professional performance and personal wellbeing. Consider implementing stress management strategies and seeking support to prevent further deterioration of your health.';
      } else if (level === 'moderate') {
        return 'Your burnout level is moderate, suggesting you are experiencing some work-related stress and fatigue. While not at critical levels, addressing these early can prevent escalation. Consider work-life balance adjustments and stress management techniques.';
      } else {
        return 'Your burnout assessment shows low burnout risk, indicating you are managing work stress effectively and maintaining good professional wellbeing. Continue maintaining the practices that support your resilience and job satisfaction.';
      }
    }
    
    // DASS-21 (DEPRESSION, ANXIETY, STRESS)
    if (config.assessmentType === 'emotio-psycho-rational' && config.selectedCategory === 1) {
      const symptom = config.results?.symptomType?.toLowerCase() || 'symptoms';
      if (level === 'high') {
        return `Your ${symptom} score is elevated, indicating more severe symptoms that may be affecting your daily functioning. Professional mental health support is recommended to develop coping strategies and treatment plans tailored to your needs.`;
      } else if (level === 'moderate') {
        return `Your ${symptom} score is moderate, suggesting you are experiencing some challenges. Incorporating stress management techniques, self-care practices, and speaking with a mental health professional could help.`;
      } else {
        return `Your ${symptom} score is low, indicating you are managing well. Continue the practices that support your mental wellbeing and maintain your current healthy coping strategies.`;
      }
    }
    
    // PHQ-9 (DEPRESSION)
    if (config.assessmentType === 'emotio-psycho-rational' && config.selectedCategory === 2) {
      if (level === 'high') {
        return 'Your depression score indicates moderate to severe symptoms that warrant professional attention. Speaking with a mental health provider about treatment options, including therapy or medication, is strongly recommended.';
      } else if (level === 'moderate') {
        return 'Your depression score suggests mild to moderate symptoms. Engaging in regular physical activity, maintaining social connections, and considering professional support can help improve your mood and overall wellbeing.';
      } else {
        return 'Your depression score is low, indicating minimal depressive symptoms. Continue the activities and practices that support your mental health and emotional wellbeing.';
      }
    }
    
    // GAD-7 (ANXIETY)
    if (config.assessmentType === 'emotio-psycho-rational' && config.selectedCategory === 3) {
      if (level === 'high') {
        return 'Your anxiety score indicates moderate to severe symptoms that are likely impacting your daily life. Professional support from a mental health provider, including therapy or cognitive-behavioral techniques, can help you manage these symptoms effectively.';
      } else if (level === 'moderate') {
        return 'Your anxiety score suggests you are experiencing some worry and tension. Relaxation techniques, mindfulness practice, and regular exercise can help reduce anxiety levels. Consider speaking with a professional if symptoms persist.';
      } else {
        return 'Your anxiety score is low, indicating you are managing worry and stress effectively. Maintain your current healthy coping mechanisms and self-care practices.';
      }
    }
    
    // FINANCIAL ASSESSMENT
    if (config.assessmentType === 'financial') {
      const selectedCategory = config.selectedCategory;
      let categoryName = 'Financial';
      
      if (selectedCategory === 1) categoryName = 'Budgeting';
      else if (selectedCategory === 2) categoryName = 'Savings';
      else if (selectedCategory === 3) categoryName = 'Investment';
      
      if (level === 'high') {
        return `Your ${categoryName.toLowerCase()} assessment shows low financial distress and good financial management in this area. Your current practices are working well; continue maintaining these positive financial habits.`;
      } else if (level === 'moderate') {
        return `Your ${categoryName.toLowerCase()} assessment indicates moderate financial challenges. There is room for improvement in your financial management strategies. Consider reviewing your approach and exploring tools or professional advice to strengthen this area.`;
      } else {
        return `Your ${categoryName.toLowerCase()} assessment shows high financial distress. Addressing these challenges early is important. Consider seeking guidance from a financial advisor or exploring resources that can help improve your financial situation.`;
      }
    }
    
    // IPAQ (PHYSICAL ACTIVITY)
    if (config.assessmentType === 'physical-ipaq') {
      const ipaq = config.scores?.ipaq;
      if (level === 'high') {
        return `Your physical activity level is high (${ipaq?.totalMET || 'high'} MET-min/week), which is excellent for cardiovascular health. Continue maintaining this level of activity while ensuring proper rest and recovery.`;
      } else if (level === 'moderate') {
        return `Your physical activity level is moderate. While you are getting some activity, increasing the frequency or intensity could provide greater health benefits. Consider gradually building up your exercise routine.`;
      } else {
        return `Your physical activity level is low. Increasing daily movement and structured exercise can significantly improve your energy levels, cardiovascular health, and overall wellbeing. Start with small, manageable increases in activity.`;
      }
    }
    
    // PSQI (SLEEP QUALITY)
    if (config.assessmentType === 'physical-psqi') {
      const psqi = config.scores?.psqi;
      if (level === 'high') {
        return `Your sleep quality is poor (PSQI score: ${psqi?.globalScore || 'high'}/21), indicating significant sleep disturbances that may be affecting your daily functioning. Consider speaking with a healthcare provider about sleep improvement strategies or evaluation for sleep disorders.`;
      } else if (level === 'moderate') {
        return `Your sleep quality is moderate, suggesting some sleep disturbances. Improving sleep hygiene habits–such as maintaining a consistent schedule and reducing screen time before bed–can help enhance your sleep quality.`;
      } else {
        return `Your sleep quality is good (PSQI score: ${psqi?.globalScore || 'low'}/21), indicating you are getting restorative sleep. Continue maintaining the bedtime routines and practices that support quality sleep.`;
      }
    }
    
    // SPIRITUAL ASSESSMENT
    if (config.assessmentDisplayName?.toLowerCase().includes('spiritual') || config.assessmentType === 'spiritual') {
      if (level === 'high') {
        return 'Your spiritual wellbeing score is high, indicating a strong sense of spiritual connection and meaning. This foundation can serve as a source of resilience and guidance. Continue nurturing your spiritual practices.';
      } else if (level === 'moderate') {
        return 'Your spiritual wellbeing shows room for growth. Exploring or deepening spiritual practices that resonate with you can enhance your sense of meaning and connection to something larger than yourself.';
      } else {
        return 'Your spiritual wellbeing assessment suggests exploring spiritual dimensions of life. Whether through meditation, religious practice, or connecting with nature, developing a spiritual practice can provide meaning and resilience.';
      }
    }
    
    // DEFAULT EXPLANATION
    if (level === 'high') {
      return 'Your assessment results indicate high performance in this area. Continue maintaining the practices that are contributing to these positive results.';
    } else if (level === 'moderate') {
      return 'Your assessment results show moderate performance. There are opportunities for improvement in this area. Consider the recommendations provided to enhance your outcomes.';
    } else {
      return 'Your assessment results indicate challenges in this area. The recommendations provided are designed to help you improve and move toward better health and wellbeing.';
    }
  };

  const handleRedoAssessment = () => {
    navigate(config.redoRoute);
  };

  const handleComplete = () => {
    navigate(config.completeRoute);
  };

  return (
    <div className="assessment-completion">
      <div className="completion-container">
        {/* Left Panel - Title and Result Card */}
        <div className="completion-results-panel completion-left-panel">
          <div className="completion-results">
            <h2>{config.resultsTitle}</h2>

            {config.assessmentType !== 'physical-general' && (
            <div className="result-card">
              <div className="result-header">
                <span className="assessment-type">
                  {(() => {
                    // Force label for CBI
                    if (config.assessmentDisplayName?.toLowerCase().includes('cbi')) {
                      return 'CBI (Copenhagen Burnout Inventory) Assessment';
                    }
                    // Check for financial assessments and display correct category
                    if (config.assessmentType === 'financial') {
                      const selectedCategory = config.selectedCategory;
                      switch (selectedCategory) {
                        case 1: return 'BUDGETING ASSESSMENT';
                        case 2: return 'SAVINGS ASSESSMENT';
                        case 3: return 'INVESTMENT ASSESSMENT';
                        default: return config.assessmentDisplayName;
                      }
                    }
                    // Check for emotio-psycho-rational assessments and display correct category
                    if (config.assessmentType === 'emotio-psycho-rational') {
                      const selectedCategory = config.selectedCategory;
                      const categoryNum = Number(selectedCategory);
                      switch (categoryNum) {
                        case 1: return 'DASS-21 ASSESSMENT';
                        case 2: return 'PHQ-9 ASSESSMENT';
                        case 3: return 'GAD-7 ASSESSMENT';
                        case 4: return 'GENDER SENSITIVITY ASSESSMENT';
                        default: return config.assessmentDisplayName;
                      }
                    }
                    return config.assessmentDisplayName;
                  })()}
                </span>

                {/* Debug: log dot count and detection for DASS-21 */}
                {(() => {
                  const isMentalHealthSymptomAssessment = config.assessmentType === 'emotio-psycho-rational' &&
                    (config.scores.category1 !== undefined || config.scores.category2 !== undefined || config.scores.category3 !== undefined || config.scores.subscales !== undefined);
                  if (isMentalHealthSymptomAssessment) {
                    const dotCount = getScoreDots(config.results.level, config.scores.overall, config.maxScore);
                    console.log('[DASS-21 Debug] Dot rendering:', {
                      assessmentType: config.assessmentType,
                      displayName: config.assessmentDisplayName,
                      level: config.results.level,
                      dotCount,
                      scores: config.scores
                    });
                  }
                  return null;
                })()}
                {!config.hideScore && (
                  <div className="score-dots">
                    {(() => {
                      let dotCount = getScoreDots(config.results.level, config.scores.overall, config.maxScore);
                      // For PHQ-9, use filledDots and color if present
                      let dotColor = getScoreColor(config.results.level);
                      if (config.assessmentType === 'emotio-psycho-rational' && config.selectedCategory === 2 && config.results.color) {
                        dotColor = config.results.color;
                      }
                      // Force orange to #e85818
                      if (dotColor === '#ea580c' || dotColor === '#ffa108' || dotColor.toLowerCase() === 'orange') {
                        dotColor = '#e85818';
                      }
                      return Array.from({ length: 3 }, (_, i) => (
                        <div
                          key={i}
                          className={`score-dot ${i < dotCount ? 'filled' : ''}`}
                          style={{ backgroundColor: i < dotCount ? dotColor : '#e5e7eb' }}
                        />
                      ));
                    })()}
                  </div>
                )}
              </div>

              {/* Score display - hidden for assessments with hideScore flag */}
              {!config.hideScore && (
                <>
                  {/* Gender Sensitivity custom label logic */}
                  <div className="result-score" style={{ color: getScoreColor(config.results.level) }}>
                    {(() => {
                      // PHQ-9: Use interpretation as label if present
                      if (config.assessmentType === 'emotio-psycho-rational' && config.selectedCategory === 2 && config.results.interpretation) {
                        return config.results.interpretation;
                      }
                      // Financial assessment: use subtitleMapping for label (high score = Low Financial Distress, low score = High Financial Distress)
                      if (config.assessmentType === 'financial') {
                        // Ensure levelKey is typed as 'High' | 'Moderate' | 'Low' for subtitleMapping
                        let levelKey: 'High' | 'Moderate' | 'Low' = 'Moderate';
                        const rawLevel = (config.results.level || '').toLowerCase();
                        if (rawLevel === 'high') levelKey = 'High';
                        else if (rawLevel === 'low') levelKey = 'Low';
                        else if (rawLevel === 'moderate') levelKey = 'Moderate';
                        if (config.subtitleMapping && config.subtitleMapping[levelKey]) {
                          return config.subtitleMapping[levelKey];
                        }
                        return `${config.results.level} Financial Distress`;
                      }
                      // MSPSS: Multidimensional Scale of Perceived Social Support
                      const isMSPSS = (config.assessmentDisplayName?.toLowerCase().includes('multidimensional scale of perceived social support') || config.selectedCategory === 2);
                      if (isMSPSS) {
                        switch (config.results.level.toLowerCase()) {
                          case 'high': return 'High Social Support';
                          case 'moderate': return 'Moderate Social Support';
                          case 'low': return 'Low Social Support';
                          default: return `${config.results.level} Social Support`;
                        }
                      }
                      // Employee Engagement custom label logic
                      const isEngagementAssessment = config.assessmentType === 'employee-engagement' || 
                        config.assessmentDisplayName?.toLowerCase().includes('engagement');
                      if (isEngagementAssessment) {
                        switch (config.results.level) {
                          case 'High': return 'Low Burnout Risk';
                          case 'Moderate': return 'Moderate Burnout Risk';
                          case 'Low': return 'High Burnout Risk';
                          default: return `${config.results.level} Employee Engagement`;
                        }
                      }
                      // Financial Assessment custom label logic
                      const isFinancialAssessment = config.assessmentType === 'financial';
                      if (isFinancialAssessment) {
                        return config.subtitleMapping[config.results.level as keyof typeof config.subtitleMapping] || `${config.results.level} ${config.scoreLabel}`;
                      }
                      return `${config.results.level} ${config.scoreLabel}`;
                    })()}
                  </div>
                </>
              )}

              <div className="result-subtitle">
                {(() => {
                  // GENERAL PHYSICAL ASSESSMENT: Show health status interpretation (no scoring)
                  if (config.assessmentType === 'physical-general') {
                    if (config.results.healthStatus) {
                      return config.results.healthStatus;
                    }
                    // Fallback if healthStatus is not available
                    return 'Your physical health assessment is complete. Review the recommendations below for personalized guidance.';
                  }
                  // IPAQ-SF PHYSICAL ACTIVITY ASSESSMENT: Rubric-based subtitle
                  if (config.assessmentType === 'physical-ipaq') {
                    const ipaq = config.scores?.ipaqSf;
                    if (ipaq) {
                      let subtitle = `Your physical activity level is ${ipaq.activityLevel.toLowerCase()} (${ipaq.totalMET} MET-min/week). `;
                      if (ipaq.sittingLevel === 'Red') {
                        subtitle += 'However, you have high sitting time (>8 hours/day) which may impact your health.';
                      } else if (ipaq.sittingLevel === 'Yellow') {
                        subtitle += 'Your sitting time is moderate (4-8 hours/day).';
                      } else {
                        subtitle += 'Your sitting time is within healthy limits (<4 hours/day).';
                      }
                      return subtitle;
                    }
                  }
                  // PSQI SLEEP QUALITY ASSESSMENT: Rubric-based subtitle
                  if (config.assessmentType === 'physical-psqi') {
                    const psqi = config.scores?.psqi;
                    if (psqi) {
                      return `Your sleep quality is ${psqi.sleepQuality.toLowerCase()} (PSQI score: ${psqi.globalScore}/21).`;
                    }
                  }
                  // PHQ-9: Show a concise subtitle with depression severity and support system
                  if (config.assessmentType === 'emotio-psycho-rational' && config.selectedCategory === 2 && config.results.interpretation) {
                    // ...existing code...
                  }
                  // DASS-21: Show structured subscale breakdown with proper formatting
                  if (
                    config.assessmentType === 'emotio-psycho-rational' &&
                    (config.selectedCategory === 1 || config.assessmentDisplayName?.toLowerCase().includes('dass-21'))
                  ) {
                    const interpretation = config.results.interpretation;
                    console.log('[DASS-21 Debug] Interpretation:', interpretation, 'Type:', typeof interpretation);
                    if (interpretation && typeof interpretation === 'object' && interpretation.subscales) {
                      // Helper function to get severity class
                      const getSeverityClass = (level: string) => {
                        switch (level) {
                          case 'Normal':
                            return 'severity--normal';
                          case 'Mild':
                          case 'Moderate':
                            return 'severity--mild-moderate';
                          case 'Severe':
                          case 'Extremely Severe':
                            return 'severity--severe';
                          default:
                            return '';
                        }
                      };

                      return (
                        <div className="dass-21-breakdown">
                          <div className="dass-21-title">DASS-21 Subscale Breakdown:</div>
                          <div className="dass-21-content">
                            <div className="dass-21-subscales">
                              <div className="subscale-item">
                                <span className="subscale-name">Depression:</span>
                                <span className={`subscale-score ${getSeverityClass(interpretation.subscales.depression.level)}`}>
                                  {interpretation.subscales.depression.score}
                                </span>
                                <span className="subscale-level">
                                  ({interpretation.subscales.depression.level}
                                  {interpretation.subscaleInterpretations && ` - ${interpretation.subscaleInterpretations.depression}`})
                                </span>
                              </div>
                              <div className="subscale-item">
                                <span className="subscale-name">Anxiety:</span>
                                <span className={`subscale-score ${getSeverityClass(interpretation.subscales.anxiety.level)}`}>
                                  {interpretation.subscales.anxiety.score}
                                </span>
                                <span className="subscale-level">
                                  ({interpretation.subscales.anxiety.level}
                                  {interpretation.subscaleInterpretations && ` - ${interpretation.subscaleInterpretations.anxiety}`})
                                </span>
                              </div>
                              <div className="subscale-item">
                                <span className="subscale-name">Stress:</span>
                                <span className={`subscale-score ${getSeverityClass(interpretation.subscales.stress.level)}`}>
                                  {interpretation.subscales.stress.score}
                                </span>
                                <span className="subscale-level">
                                  ({interpretation.subscales.stress.level}
                                  {interpretation.subscaleInterpretations && ` - ${interpretation.subscaleInterpretations.stress}`})
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }
                  }
                  if (typeof config.results.interpretation === 'string') {
                    return config.results.interpretation;
                  }
                  return config.results.interpretation;
                })()}
              </div>
            </div>
            )}

          </div>
        </div>

        {/* Right Panel - Recommendations and Actions */}
        <div className="completion-recommendations-panel">
          <div className="completion-recommendations-content">
            {/* RECOMMENDED NEXT STEPS BOX */}
            <div className="result-description">
              {/* Show recommendations if available */}
              {(() => {
                // For CBI, always use backend's level and recommendations for all risk levels
                const isCBIAssessment = config.assessmentDisplayName?.toLowerCase().includes('cbi') || config.assessmentDisplayName?.toLowerCase().includes('burnout');
                if (isCBIAssessment) {
                  return (
                    <div className="recommendations">
                      <h4>Recommended Next Steps</h4>
                      <ul>
                        {config.results.recommendations && config.results.recommendations.map((rec: Recommendation, index: number) => {
                          if (typeof rec === 'string') {
                            return <li key={index}>{rec}</li>;
                          } else if (rec && typeof rec === 'object') {
                            return (
                              <li key={index}>
                                <strong>{rec.title || rec.category}:</strong> {rec.description}
                                {rec.priority && <span className={`priority-badge priority-badge-${rec.priority}`}>{rec.priority} priority</span>}
                              </li>
                            );
                          }
                          return null;
                        })}
                      </ul>
                    </div>
                  );
                }
                // Default recommendations rendering
                if (config.results.recommendations && config.results.recommendations.length > 0) {
                  return (
                    <div className="recommendations">
                      <h4>Recommended Next Steps</h4>
                      <ul>
                        {config.results.recommendations.map((rec: Recommendation, index: number) => {
                          if (typeof rec === 'string') {
                            return <li key={index}>{rec}</li>;
                          } else if (rec && typeof rec === 'object') {
                            return (
                              <li key={index}>
                                <strong>{rec.title || rec.category}:</strong> {rec.description}
                                {rec.priority && <span className={`priority-badge priority-badge-${rec.priority}`}>{rec.priority} priority</span>}
                              </li>
                            );
                          }
                          return null;
                        })}
                      </ul>
                    </div>
                  );
                }
                return null;
              })()}
            </div>

            <div className="completion-actions">
              <button
                className="action-button redo-button"
                onClick={handleRedoAssessment}
              >
                <span className="material-symbols-outlined redo-icon">restart_alt</span>
                Redo Assessment
              </button>

              <button
                className="action-button complete-button"
                onClick={handleComplete}
              >
                Complete
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssessmentCompletionTemplate;
